import { isHolidayOrWeekend, nextBusinessDay } from '@freightview/holidays'

import {
  type FvDate,
  type FvDateInput,
  type FvManipulateUnit,
  fvDate,
  fvDateUtc,
  fvMaxDate,
} from '@fv/dates'

import {
  type DateRange,
  dateRangeNames,
  type PredefinedDateRange,
} from '../models/date-range.ts'

type ParseableDate = Date | FvDateInput | string
type DateRangeResult = {
  start: FvDate
  end: FvDate
  name: PredefinedDateRange | 'custom'
}

const rangeRegex = /^(-?\d+)-(-?\d+)$/

const dateRangeBuilders: Record<
  PredefinedDateRange,
  () => Omit<DateRangeResult, 'name'>
> = {
  'last-30-days': () => {
    const start = fvDateUtc().subtract(1, 'month').startOf('day')
    const end = fvDateUtc().endOf('day')
    return { start, end }
  },
  'last-7-days': () => {
    const start = fvDateUtc().subtract(1, 'week').startOf('day')
    const end = fvDateUtc().endOf('day')
    return { start, end }
  },
  'next-week': () => {
    const start = fvDateUtc().add(1, 'week').startOf('week').startOf('day')

    const end = fvDateUtc().add(1, 'week').endOf('week').endOf('day')
    return { start, end }
  },
  'this-month': () => {
    const start = fvDateUtc().startOf('month').startOf('day')
    const end = fvDateUtc().endOf('month').endOf('day')
    return { start, end }
  },
  'this-week': () => {
    const start = fvDateUtc().startOf('week').startOf('day')
    const end = fvDateUtc().endOf('week').endOf('day')
    return { start, end }
  },
  today: () => {
    const start = fvDateUtc().startOf('day')
    const end = fvDateUtc().endOf('day')
    return { start, end }
  },
  tomorrow: () => {
    const start = fvDateUtc().add(1, 'day').startOf('day')
    const end = fvDateUtc().add(1, 'day').endOf('day')
    return { start, end }
  },
  yesterday: () => {
    const start = fvDateUtc().subtract(1, 'day').startOf('day')
    const end = fvDateUtc().subtract(1, 'day').endOf('day')
    return { start, end }
  },
  'last-3-months': () => {
    const start = fvDateUtc().subtract(3, 'month').startOf('day')
    const end = fvDateUtc().endOf('month').endOf('day')
    return { start, end }
  },
  'last-month': () => {
    const month = fvDateUtc().subtract(1, 'month')
    const start = month.startOf('month').startOf('day')
    const end = month.endOf('month').endOf('day')
    return { start, end }
  },
  'last-year': () => {
    const year = fvDateUtc().subtract(1, 'year')
    const start = year.startOf('year').startOf('day')
    const end = year.endOf('year').endOf('day')
    return { start, end }
  },
  'last-12-months': () => {
    const year = fvDateUtc().subtract(1, 'year')
    const start = year.startOf('day')
    const end = fvDateUtc().endOf('day')
    return { start, end }
  },
  'this-year': () => {
    const start = fvDateUtc().startOf('year').startOf('day')
    const end = fvDateUtc().endOf('year').endOf('day')
    return { start, end }
  },
}

export const dates = {
  now: () => fvDateUtc().toDate(),
  threeYearsFromNow: () => fvDateUtc().add(3, 'years').toDate(),
  threeYearsAgo: () => fvDateUtc().subtract(3, 'years').toDate(),
  sixMonthsAgo: () => fvDateUtc().subtract(6, 'months').toDate(),
  nineteyDaysAgo: () => fvDateUtc().subtract(90, 'days').toDate(),
  sixtyDaysAgo: () => fvDateUtc().subtract(60, 'days').toDate(),
  twoWeeksAgo: () => fvDateUtc().subtract(14, 'days').toDate(),
  thirtyDaysAgo: () => fvDateUtc().subtract(30, 'days').toDate(),
  yesterday: () => fvDateUtc().subtract(1, 'days').toDate(),
  isToday: (date: ParseableDate) => fvDateUtc(date).isSame(dates.now(), 'day'),
  daysAway: (date: ParseableDate) => fvDateUtc(date).diff(dates.now(), 'day'),

  isBefore(date: ParseableDate, compareDate: ParseableDate) {
    return fvDateUtc(date).isBefore(compareDate)
  },

  isBeforeTime(time: string, compareTime: string) {
    return this.timeTofvDate(time).isBefore(this.timeTofvDate(compareTime))
  },

  timeTofvDate(time: string): FvDate {
    return fvDate(`${fvDate().format('YYYY-MM-DD')} ${time}`)
  },

  formatTime(time: string, timezone: string): string {
    if (!time) {
      return time
    }

    return fvDate(time, 'HH:m A', timezone).format('hh:mm A')
  },

  formatDate(date: ParseableDate, format = 'MM/DD/YYYY'): string {
    const m = fvDateUtc(date)
    if (!date || !m.isValid()) {
      return ''
    }

    return m.format(format)
  },

  max(...dates: Array<ParseableDate>): Date | undefined {
    const filtered = dates.filter(d => !!d).map(d => fvDateUtc(d))

    if (!filtered.length) {
      return
    }

    return fvMaxDate(...filtered).toDate()
  },

  subtract(date: ParseableDate, amount: number, unit: FvManipulateUnit): Date {
    return fvDateUtc(date).subtract(amount, unit).toDate()
  },

  add(date: ParseableDate, amount: number, unit: FvManipulateUnit): Date {
    return fvDateUtc(date).add(amount, unit).toDate()
  },

  today() {
    return fvDateUtc().startOf('day').toDate()
  },

  utcNoon(date: ParseableDate): Date {
    if (!date) {
      return undefined
    }

    return fvDateUtc(date).startOf('day').add(12, 'hours').toDate()
  },

  startOfDay(date: ParseableDate): Date {
    if (!date) {
      return undefined
    }

    return fvDateUtc(date).startOf('day').toDate()
  },

  endOfDay(date: ParseableDate): Date {
    if (!date) {
      return undefined
    }

    return fvDateUtc(date).endOf('day').toDate()
  },

  nextAvailablePickupDate() {
    return fvDateUtc(nextBusinessDay(dates.today())).add(12, 'hours').toDate()
  },

  isDayInPast(date: ParseableDate) {
    return fvDateUtc(date).startOf('day').isBefore(fvDateUtc().startOf('day'))
  },

  getEstimatedDeliveryDate(
    pickupDate?: string | Date,
    quoteProps?: { transitDaysMin?: number; deliveryDate?: string | Date },
  ) {
    let { transitDaysMin, deliveryDate } = quoteProps ?? {}
    if (deliveryDate) {
      return fvDate(deliveryDate)
    }

    if (!transitDaysMin || !pickupDate) {
      return
    }

    let incrementDate = 1

    while (incrementDate <= transitDaysMin) {
      const day = fvDate(pickupDate).add(incrementDate, 'days')
      if (isHolidayOrWeekend(day.toDate())) {
        transitDaysMin += 1
      }
      incrementDate += 1
    }

    return fvDate(pickupDate).add(transitDaysMin, 'days')
  },

  isValidRange(val: string): val is DateRange {
    return (
      dateRangeNames.includes(val as PredefinedDateRange) ||
      rangeRegex.test(val)
    )
  },

  isPredefinedRange(val: string): val is PredefinedDateRange {
    return dateRangeNames.includes(val as PredefinedDateRange)
  },

  parseDateRange(val: DateRange): DateRangeResult {
    let start: FvDate | undefined = undefined
    let end: FvDate | undefined = undefined

    if (dates.isPredefinedRange(val)) {
      const range = dateRangeBuilders[val]()
      start = range.start
      end = range.end

      return { start, end, name: val }
    }

    const matches = val.match(rangeRegex)
    if (matches?.length > 2) {
      start = fvDateUtc(Number(matches[1])).startOf('day')
      end = fvDateUtc(Number(matches[2])).endOf('day')
    }
    return {
      start,
      end,
      name: 'custom',
    }
  },

  dateToStringWithLocalTimezoneAbbr(
    date?: FvDateInput,
    format = 'ddd, MMM DD hh:mm a',
  ) {
    if (!date) return null
    const tz = new Date()
      .toLocaleTimeString('en-us', { timeZoneName: 'short' })
      .split(' ')[2]

    return fvDate(date).format(`${format} [${tz}]`)
  },
}
